import React, { useRef, useState, useEffect } from "react";

import { ReactComponent as Arrow } from '@assets/images/animated-links/arrow.svg';

interface SVGElementInterface extends Element {
  beginElement(): SVGElement;
}

type AnimatedLinksProps = {
  title?: string;
  body?: string;
  link?: string;
  href?: string;
  size: string;
  id?: string;
  className?: string;
  isDesktopOrLaptop?: any;
};

const bgSizes: { [key: string]: any } = {
  'sm': {
    hover: 'M 20,20 705,0 720,238 0,208 Z',
    default: 'M 0,0 720,30 700,218 15,238 Z',
  },
  'md': {
    hover: 'M 20,19.156 705,0 720,245 0,216.2662 Z',
    default: 'M 0,0 720,28.7338 700,225.844 15,245 Z',
  },
  'lg': {
    hover: 'M 20,19.156 705,0 720,295 0,266.2662 Z',
    default: 'M 0,0 720,28.7338 700,275.844 15,295 Z',
  },
};

const imageSizes: { [key: string]: any } = {
  'sm': {
    hover: 'M 18.496779,19.59779 267.9185,10.945223 254.59372,181.6747 1.8177843,171.05922 Z',
    default: 'M 0.23203769,0.23203769 253.84923,10.209658 l 0,175.420502 -241.551232,11.1378 z',
  },
  'md': {
    hover: 'M 14.886033,14.798322 248.28385,12.030293 238.43906,188.57644 1.0497341,181.34082 Z',
    default: 'M -0.24381625,0 253.81272,10.484099 l 0,187.982321 -241.378091,8.28976 z',
  },
  'lg': {
    hover: 'M 16.64812,18.493538 253.31329,15.175085 244.47626,237.77348 1.0312425,229.52 Z',
    default: 'M -0.29472477,0 254.05275,13.852064 253.75803,240.79014 12.083715,256.70528 Z',
  },
};

const bgSquare: { [key: string]: any } = {
  'sm': {
    width: 720,
    height: 238,
  },
  'md': {
    width: 720,
    height: 245,
  },
  'lg': {
    width: 720,
    height: 295,
  },
};

const imageSquare: { [key: string]: any } = {
  'sm': {
    width: 255,
    height: 198,
  },
  'md': {
    width: 255,
    height: 208,
  },
  'lg': {
    width: 255,
    height: 258,
  },
};

const rectMask: { [key: string]: any } = {
  'sm': {
    width: 323,
    height: 219,
  },
  'md': {
    width: 323,
    height: 219,
  },
  'lg': {
    width: 391,
    height: 265,
  },
};

export const AnimatedLinks: React.FC<AnimatedLinksProps> = ({ ...props }) => {
  const {
    title,
    body,
    link,
    href,
    size,
    id,
    className,
    isDesktopOrLaptop
  } = props;
  const [active, setActive] = useState(false);

  const linkBGHover = useRef<SVGElementInterface & SVGElement>(null);
  const linkBGDefault = useRef<SVGElementInterface & SVGElement>(null);

  const linkImageHover = useRef<SVGElementInterface & SVGElement>(null);
  const linkImageDefault = useRef<SVGElementInterface & SVGElement>(null);

  const wrapper = useRef<HTMLDivElement>(null);

  const toggleActive = (bool: boolean) => {
    if (isDesktopOrLaptop) {
      setActive(bool);
      if (bool) {
        linkBGHover.current?.beginElement();
        linkImageHover.current?.beginElement();
      } else {
        linkBGDefault.current?.beginElement();
        linkImageDefault.current?.beginElement();
      }
    }
  };

  return (
    <a
      href={link}
      className={`
        ${className}
        block
        max-w-720
        mx-auto
        relative
      `}
      onMouseEnter={() => toggleActive(true)}
      onMouseLeave={() => toggleActive(false)}
    >
      <span
        className={`
          ${active? `-mt-7 mr-4` : ``}
          ${isDesktopOrLaptop ? `` : `-mt-7 mr-4`}
          transform
          transition-all
          duration-600

          absolute
          top-0
          right-0
          text-xxs
          text-white
          pl-5
        `}
      >Редакционный материал</span>
      <svg
        className={`
          ${isDesktopOrLaptop ? `` : `w-full min-h-111 -mt-7`}
          absolute
        `}
        preserveAspectRatio="xMidYMin slice"
        width={`${bgSquare[size].width}`}
        height={`${bgSquare[size].height}`}
        viewBox={`0 0 ${bgSquare[size].width} ${bgSquare[size].height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d={bgSizes[size].default}
          fill="#65A3FF"
          className={`
            ${active? `opacity-100` : `opacity-90`}
          `}
        >
          <animate
            ref={linkBGHover}
            id="bg-hover"
            begin="indefinite"
            fill="freeze"
            attributeName="d"
            dur="500ms"
            to={bgSizes[size].hover}
          />
          <animate
            ref={linkBGDefault}
            id="bg-default"
            begin="indefinite"
            fill="freeze"
            attributeName="d"
            dur="500ms"
            to={bgSizes[size].default}
          />
        </path>
      </svg>

      <div
        className={`
          relative
          items-center
          px-5
          lg:px-5
          py-5
          flex
          flex-col
          lg:flex-row
        `}
      >
        <svg
          preserveAspectRatio="xMidYMin slice"
          className={`
            min-w-full
            lg:min-w-0
            mb-4
            lg:mb-0
          `}
          width={`${imageSquare[size].width}`}
          height={`${imageSquare[size].height}`}
          viewBox={`0 0 ${imageSquare[size].width} ${imageSquare[size].height}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <mask
            id={`maskLink${id}`}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={`${imageSquare[size].width}`}
            height={`${imageSquare[size].height}`}
          >
            <path
              d={imageSizes[size].default}
              fill="#FFFFFF"
            >
              <animate
                ref={linkImageHover}
                id="link-hover"
                begin="indefinite"
                fill="freeze"
                attributeName="d"
                dur="500ms"
                to={imageSizes[size].hover}
              />
              <animate
                ref={linkImageDefault}
                id="link-default"
                begin="indefinite"
                fill="freeze"
                attributeName="d"
                dur="500ms"
                to={imageSizes[size].default}
              />
            </path>
          </mask>
          <g mask={`url(#maskLink${id})`}>
            <rect
              x="-55"
              y="-7"
              width={rectMask[size].width}
              height={rectMask[size].height}
              fill={`url(#patternLink${id})`}
            />
          </g>
          <defs>
            <pattern id={`patternLink${id}`} patternContentUnits="objectBoundingBox" width="1" height="1">
              <use xlinkHref={`#imageLink${id}`} transform="translate(-0.102981) scale(0.00133996 0.00197628)" />
            </pattern>
            <image
              id={`imageLink${id}`}
              width="850"
              height="506"
              xlinkHref={href}
            />
          </defs>
        </svg>
        <div
          className={`
            ${active? `-mt-7` : ``}
            transform
            transition-all
            duration-600
            flex-1
            text-white
            px-3
            lg:pl-5
          `}
        >
          <h3
            className={`
              font-medium
              lg:font-bold
              text-md
              lg:text-lglg
              mb-2
            `}
          >
            {title}
          </h3>
          <p
            className={`
              text-sm
              lg:text-md
              leading-6
              mb-2
              lg:mb-3
            `}
          >
            {body}
          </p>
          <span
            className={`
              relative
              flex
              transform
              transition-all
              duration-600

              ${active? `left-full -translate-x-1/3` : `left-0`}

              items-center
              font-bold
              text-white
            `}
          >
            <span
              className={`
                underline
                mr-4
              `}
            >ЧИТАТЬ</span><Arrow />
          </span>
        </div>
      </div>
    </a>
  );
}